import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import * as React from "react";
import {
    AnnualPolicyPremiumTypeEnum,
    ICacheManager,
    Types
} from "@ferry/journey-services";
import {Navigate} from "react-router";
import FalconHeader from "../../../../../../components/navigation/FalconHeader";
import {PluginTemplates} from "../../../../../../utils/PluginTemplates";
import {lazyInject} from "@athena-portal/core";
import {MigratedCreditCardSection} from "../MigratedCreditCardSection";
import {Checkbox} from "../../../../../../components/inputs/Checkbox";
import {AutoRenewalBenefits} from "@ferry/plugin-ferry-journey";
import {FalconAccordion} from "../../../../../../components/FalconAccordion";
import {MigratedPaymentScheduleSection} from "./MigratedPaymentScheduleSection";
import {Types as CustomerPortalTypes} from "../../../../../../Types";
import type {ICustomerPortalService} from "../../../../../../services/ICustomerPortalService";
import {
    InfoBanner,
    WarningBanner,
    FalconNavigatorHeader,
    resourcesHelper,
    Footer
} from "@ferry/portal-core";

interface IParams {
    policyNumber: string;
    [key: string]: string | undefined
}

interface IState {
    interaction?: any;
    interactionFound?: boolean;
    loading?: boolean;
}

@authorize
export class MigratedRenewalPaymentDetailsPage extends React.Component<IWithRouterProps<IParams>, IState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(CustomerPortalTypes.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    @lazyInject(Types.CacheManager)
    private cacheManager: ICacheManager;

    constructor(props: IWithRouterProps<IParams>) {
        super(props);

        this.state = {
            loading: true
        };

        this.pluginTemplates.setPageTitle("Payment details");
    }

    public componentDidMount(): void {
        this.cacheManager.clearCache("getPolicy");
        this.cacheManager.clearCache("getProfile");

        this.customerPortalService.getInteractionByStatus(['MIGRATED_RENEWAL_ACCEPTED'])
            .subscribe((interaction: any) => {
                if (interaction) {
                    this.setState({interaction, loading: false, interactionFound: true});
                } else {
                    this.setState({loading: false, interactionFound: false});
                }
            })
    }

    public render() {
        const {interaction} = this.state;

        if (this.state.interactionFound === false) {
            return <Navigate to={`/dashboard/migratedRenewal`}/>
        } else if (this.state.loading) {
            return <Overlay show={true}/>;
        } else if (this.state.interactionFound) {
            return this.renderPageContent(interaction);
        }

        return <></>
    }

    private renderPageContent(interaction: any) {
        return (
            <div className="dashboard payment-details">

                <FalconHeader/>

                <div className="plugin">
                    <FalconNavigatorHeader
                        back="/dashboard/migratedRenewal"
                        title="Payment details"
                    />

                    <div className="mt-3 margin-top-0 grid-col-2">
                        <InfoBanner wording={<>If you need to amend your payment details for your renewal please go to your renewal quote tile.</>}/>
                    </div>

                    <div className="panel bg-white overflow-hidden p-0 font-weight-extra-light">
                        <div className="payment-details-table">
                                {interaction.factors.premiumType === AnnualPolicyPremiumTypeEnum.MONTHLY &&
                                    this.renderMonthlyPaymentDetailsContent(interaction)
                                }

                                {interaction.factors.premiumType === AnnualPolicyPremiumTypeEnum.ANNUAL &&
                                    this.renderAnnualPaymentDetailsContent(interaction)
                                }
                        </div>
                    </div>

                    <>
                        <div className="mt-5 inner-container automatic-renewal-checkbox-section">
                            <h2 className="fs-18 font-family-display font-weight-semi-bold text-blue-2">Auto-renewal
                                makes
                                things a little easier...</h2>
                            <AutoRenewalBenefits/>
                            <div className="expandable-section">
                                <p className="font-family-base font-weight-semi-bold text-blue-2 fs-16 mb-0">Prefer not
                                    to
                                    auto-renew?</p>
                                <div className="widget">
                                    <FalconAccordion
                                        toggleHidden={this.renderAccordionToggle("expand-icon")}
                                        toggleShown={this.renderAccordionToggle("collapse-icon")}
                                        togglePositionTop={true}>
                                        <div>
                                            <Checkbox
                                                className="ml-0 mt-0"
                                                id="automaticRenewalCheckbox"
                                                name="automaticRenewal"
                                                label={this.getCheckboxLabel()}
                                                value={interaction.policyHolder.performRenewal}
                                                disabled={true}
                                                onChanged={this.emptyChangeAction}
                                            />
                                        </div>
                                    </FalconAccordion>
                                </div>
                            </div>
                        </div>
                    </>
                    {this.pluginTemplates.renderBackLink("Back", false, `/dashboard/migratedRenewal`)}
                </div>
                <Footer/>
            </div>
        );
    }

    private renderAnnualPaymentDetailsContent(interaction: any): any {
        const mainPaymentCard = this.getMainCardDetails(interaction.policyHolder.cardDetails);
        return (
        <div className="padding-32">
            <div className="payment-card-section">
                <div className="d-flex flex-row">
                    <div className="d-none d-md-flex flex-column">
                        <img className="icon-panel mb-0"
                             src={`/${resourcesHelper.THEME_PATH}/img/payment-wallet.svg`}
                             alt="Credit card icon" aria-label="Credit card icon"/>
                    </div>
                    <div className="d-flex flex-column justify-c-center">
                        <div className="d-md-none d-flex align-i-center mb-3">
                            <img className="icon-panel mb-0"
                                 src={`/${resourcesHelper.THEME_PATH}/img/payment-wallet.svg`}
                                 alt="Credit card icon" aria-label="Credit card icon"/>
                            <h2 className="font-family-display font-weight-bold mb-0">
                                Card details
                            </h2>
                        </div>
                        <div className="d-flex flex-column">
                            <h2 className="font-family-display font-weight-bold d-md-block d-none mb-4">
                                Card details
                            </h2>

                            <MigratedCreditCardSection mainCard={mainPaymentCard}/>
                            {mainPaymentCard &&
                                <div className="card-reuse-checkbox mb-2">
                                    <Checkbox
                                        className="ml-3"
                                        label="I consent to my card details being stored securely for any future policy changes I make."
                                        name="cardStorageConsentCheckbox"
                                        id="cardStorageConsentCheckbox"
                                        disabled={false}
                                        value={interaction.agreeToReuseCardDetails}
                                        onChanged={(value) => this.changeReuseCardAction(value, interaction)}
                                    />
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
        );
    }

    private renderMonthlyPaymentDetailsContent(interaction: any): any {
        return (
            <>
                <div className="padding-32">
                    {this.renderDirectDebitSection(interaction)}
                </div>

                <div className="padding-32">
                    <MigratedPaymentScheduleSection interaction={interaction}/>
                </div>
            </>
        );
    }

    private renderDirectDebitSection(interaction: any) {
        return (
            <div className="direct-debit-section">
                <div className="d-flex flex-row">
                    <div className="d-none d-md-flex flex-column">
                        <img className="icon-panel mb-0"
                             src={`/${resourcesHelper.THEME_PATH}/img/direct-debit-icon.svg`}
                             alt="Debit icon" aria-label="Debit icon"/>
                    </div>

                    <div className="d-flex flex-column justify-c-center">
                        <div className="d-md-none d-flex align-i-center mb-3">
                            <img className="icon-panel mb-0"
                                 src={`/${resourcesHelper.THEME_PATH}/img/direct-debit-icon.svg`}
                                 alt="Debit icon" aria-label="Debit icon"/>
                            <h2 className="font-family-display font-weight-bold mb-0">
                                Direct Debit details
                            </h2>
                        </div>

                        <div className="d-flex flex-column">
                            <h2 className="font-family-display font-weight-bold d-md-block d-none mb-4">
                                Direct Debit details
                            </h2>
                            {interaction?.paymentDetails?.directDebitDetails ?
                                <div className="d-flex flex-column justify-content-center mt-3 margin-top-0">
                                    <div className="mt-1 mb-2 margin-top-0">
                                        <b>Name of account holder: </b>
                                        {interaction?.paymentDetails?.directDebitDetails.accountHolderName}
                                    </div>
                                    <div className="mt-1 mb-2 margin-top-0">
                                        <b>Account number: </b>
                                        {'*****' + interaction?.paymentDetails?.directDebitDetails.accountNumber.toString().slice(-3)}
                                    </div>
                                    <div className="mt-1 mb-2 margin-top-0">
                                        <b>Sort code: </b>
                                        {`${String(interaction?.paymentDetails?.directDebitDetails.sortCode).substring(0, 2)} -
                                        ${String(interaction?.paymentDetails?.directDebitDetails.sortCode).substring(2, 4)} -
                                        ${String(interaction?.paymentDetails?.directDebitDetails.sortCode).substring(4, 6)}`}
                                    </div>
                                </div>
                                : <WarningBanner wording={<>You no longer have an active Direct Debit with {resourcesHelper.THEME_NAME}.</>}/>
                            }
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private getMainCardDetails(cardDetails: any[]): any {
        return cardDetails && cardDetails.length > 0 ? cardDetails[0] : undefined;
    }

    // @ts-ignore
    private emptyChangeAction() {
    }

    private changeReuseCardAction(value: boolean, interaction: any) {
        this.setState({loading: true});

        const interactionId = interaction._id;
        this.customerPortalService.setReuseCardByInteraction(interactionId, value)
            .subscribe((reuseCardValue?: boolean) => {
                interaction.agreeToReuseCardDetails = reuseCardValue === true;
                this.setState({interaction: interaction, loading: false});
            });
    }

    private getCheckboxLabel() {
        return (
            <div>
                <p>By automatically renewing your insurance, you can save time next year and ensure that there's no
                    interruption to your cover.</p>
            </div>
        );
    }

    private renderAccordionToggle(accordionIcon: string) {
        return (
            <div className="toggle text-left mt-0 mb-3 d-flex align-items-center">
                <span className="mr-1">Opt out here</span>
                <span className={`toggle-icon ${accordionIcon}`}/>
            </div>
        )
    }
}

export const MigratedRenewalPaymentDetails = withRouter<{}, IParams>(MigratedRenewalPaymentDetailsPage)
